/**
 * 拠点 `/reception/admin/locations`(reception:admin。設計書 §9.4、計画 第 3 段)。
 * 一覧(行を押すと編集)+「拠点を追加」。編集の窓では拠点メンバー(operator / viewer)を足し、
 * 期間で終わらせる(行は消さない。誰がいつ担当だったかを残す)。受付窓口へのテスト送信もここ。
 * RC1 の通知はメールだけ(D-14)なので、Slack / Teams の欄は無い。
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";

import { useApi } from "../../lib/useApi";
import { useDebounced } from "../../lib/useDebounced";
import { DataTable, PageHeader, StatusTag, TimeAgo, type DataColumn } from "../../ui";
import {
  errorText,
  useInvalidateReception,
  useLocations,
  useMembers,
  useReceptionPerms,
  useSend,
  type ChannelResult,
  type ReceptionLocation,
  type ReceptionLocationInput,
  type ReceptionMember,
} from "./hooks";

const { Text } = Typography;

const ROLE_OPTIONS = [
  { value: "operator", label: "operator(受付操作・来訪者の氏名を見る)" },
  { value: "viewer", label: "viewer(閲覧だけ。来訪者の氏名は見ない)" },
];
const roleLabel = (r: string) => (r === "operator" ? "operator" : "viewer");

export function LocationsPage() {
  const { canAdmin } = useReceptionPerms();
  const query = useLocations();
  const rows = query.data?.items ?? [];
  // null = 閉じている / "new" = 追加 / 行 = 編集
  const [editing, setEditing] = useState<ReceptionLocation | "new" | null>(null);

  const columns: DataColumn<ReceptionLocation>[] = [
    { title: "拠点", ellipsis: false, dataIndex: "name", width: 200 },
    { title: "受付窓口のメール", ellipsis: false, dataIndex: "desk_email", width: 240 },
    { title: "タイムゾーン", ellipsis: false, dataIndex: "timezone", width: 180 },
    { title: "メンバー", ellipsis: false, dataIndex: "member_count", width: 90, render: (v: number) => <span className="ui-num">{v}</span> },
    {
      title: "状態",
      ellipsis: false,
      width: 110,
      render: (_: unknown, l) =>
        l.enabled ? <StatusTag tone="success">有効</StatusTag> : <StatusTag tone="neutral">無効</StatusTag>,
    },
    { title: "更新", ellipsis: false, dataIndex: "updated_at", width: 160, render: (v: string) => <TimeAgo iso={v} /> },
  ];

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="拠点"
          description="受付をする拠点と、その拠点の担当(拠点メンバー)。operator / viewer のロールを持っていても、拠点メンバーでない拠点の来訪は見えない。"
          primary={
            canAdmin ? (
              <Button type="primary" onClick={() => setEditing("new")}>
                拠点を追加
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="拠点を取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<ReceptionLocation>
          columns={columns}
          scrollX={columns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={rows.length}
          pageSize={Math.max(rows.length, 1)}
          onPage={() => undefined}
          onRowClick={(row) => setEditing(row)}
          rowTone={(row) => (row.enabled ? undefined : "neutral")}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{ text: "拠点がない。「拠点を追加」から作る" }}
        />
      </div>
      <LocationModal
        location={editing === "new" ? null : editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onCreated={(l) => setEditing(l)}
      />
    </div>
  );
}

function LocationModal({
  location,
  open,
  onClose,
  onCreated,
}: {
  location: ReceptionLocation | null;
  open: boolean;
  onClose: () => void;
  onCreated: (l: ReceptionLocation) => void;
}) {
  return (
    <Modal
      open={open}
      width={760}
      title={location ? `拠点: ${location.name}` : "拠点を追加"}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {location ? (
        <Tabs
          items={[
            { key: "form", label: "拠点", children: <LocationForm location={location} onDone={onClose} /> },
            { key: "members", label: "拠点メンバー", children: <Members location={location} /> },
            { key: "test", label: "テスト送信", children: <TestChannels location={location} /> },
          ]}
        />
      ) : (
        <LocationForm location={null} onDone={onClose} onCreated={onCreated} />
      )}
    </Modal>
  );
}

function LocationForm({
  location,
  onDone,
  onCreated,
}: {
  location: ReceptionLocation | null;
  onDone: () => void;
  onCreated?: (l: ReceptionLocation) => void;
}) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const { readOnly } = useReceptionPerms();
  const [form] = Form.useForm<ReceptionLocationInput>();

  useEffect(() => {
    form.setFieldsValue(
      location ?? {
        name: "",
        address: "",
        timezone: "Asia/Tokyo",
        desk_email: "",
        phone_extension: "",
        recovery_contact: "",
        checkin_early_minutes: 120,
        checkin_late_minutes: 60,
        enabled: true,
      },
    );
  }, [location, form]);

  const save = useMutation({
    mutationFn: (v: ReceptionLocationInput) => {
      const body = { ...v, address: v.address ?? "", phone_extension: v.phone_extension ?? "", recovery_contact: v.recovery_contact ?? "" };
      return location
        ? send<ReceptionLocation>("PATCH", `/locations/${location.id}`, body)
        : send<ReceptionLocation>("POST", "/locations", body);
    },
    onSuccess: (l) => {
      void message.success("保存した(監査ログに記録した)");
      invalidate();
      if (location) onDone();
      else onCreated?.(l);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Form<ReceptionLocationInput>
      form={form}
      layout="vertical"
      disabled={readOnly || save.isPending}
      onFinish={(v) => save.mutate(v)}
    >
      <Form.Item name="name" label="拠点名" rules={[{ required: true, whitespace: true, message: "拠点名を入れる" }]}>
        <Input maxLength={200} />
      </Form.Item>
      <Form.Item
        name="desk_email"
        label="受付窓口のメール"
        extra="担当者に連絡がつかないときの通知先(RC1 の通知はメールだけ)"
        rules={[{ required: true, type: "email", message: "メールアドレスを入れる" }]}
      >
        <Input placeholder="reception@example.co.jp" />
      </Form.Item>
      <Space size={16} wrap align="start">
        <Form.Item name="timezone" label="タイムゾーン" rules={[{ required: true }]}>
          <Input style={{ width: 200 }} placeholder="Asia/Tokyo" />
        </Form.Item>
        <Form.Item name="phone_extension" label="内線">
          <Input style={{ width: 160 }} maxLength={50} />
        </Form.Item>
        <Form.Item name="enabled" label="有効" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Space>
      <Space size={16} wrap align="start">
        <Form.Item name="checkin_early_minutes" label="受付できる早さ(予定の何分前から)" rules={[{ required: true }]}>
          <InputNumber min={0} max={1440} addonAfter="分" />
        </Form.Item>
        <Form.Item name="checkin_late_minutes" label="遅れの許容(予定の何分後まで)" rules={[{ required: true }]}>
          <InputNumber min={0} max={1440} addonAfter="分" />
        </Form.Item>
      </Space>
      <Form.Item name="address" label="住所">
        <Input.TextArea rows={2} maxLength={2000} />
      </Form.Item>
      <Form.Item name="recovery_contact" label="端末が使えないときの連絡先" extra="受付端末の画面に出す(内線や窓口の案内)">
        <Input.TextArea rows={2} maxLength={2000} />
      </Form.Item>
      <Space>
        <Button onClick={onDone} disabled={false}>
          やめる
        </Button>
        <Button type="primary" htmlType="submit" loading={save.isPending}>
          保存
        </Button>
      </Space>
    </Form>
  );
}

function EmployeePicker({ value, onChange }: { value?: number | null; onChange?: (id: number | null) => void }) {
  const api = useApi();
  const [input, setInput] = useState("");
  const q = useDebounced(input);
  const query = useQuery({
    queryKey: ["directory", "employees", "reception-member", q],
    queryFn: () =>
      api.directoryEmployees(new URLSearchParams({ limit: "20", offset: "0", status: "active", ...(q ? { q } : {}) })),
  });
  return (
    <Select
      showSearch
      allowClear
      style={{ width: 280 }}
      placeholder="氏名 / 社員番号"
      filterOption={false}
      loading={query.isFetching}
      searchValue={input}
      onSearch={setInput}
      value={value ?? undefined}
      options={(query.data?.items ?? []).map((e) => ({ value: e.id, label: `${e.name}(${e.emp_code})` }))}
      onChange={(v) => onChange?.(v ?? null)}
      notFoundContent={query.isFetching ? "検索中…" : "該当する従業員なし"}
    />
  );
}

type MemberForm = { employee_id: number; role: "operator" | "viewer"; valid_until?: Dayjs | null };

const fmt = (iso: string | null) => (iso ? dayjs(iso).format("YYYY-MM-DD HH:mm") : "");

function Members({ location }: { location: ReceptionLocation }) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const { readOnly } = useReceptionPerms();
  const query = useMembers(location.id);
  const [form] = Form.useForm<MemberForm>();

  const add = useMutation({
    mutationFn: (v: MemberForm) =>
      send("POST", `/locations/${location.id}/members`, {
        employee_id: v.employee_id,
        role: v.role,
        ...(v.valid_until ? { valid_until: v.valid_until.toISOString() } : {}),
      }),
    onSuccess: () => {
      void message.success("拠点メンバーに足した(監査ログに記録した)");
      form.resetFields();
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      send("PATCH", `/locations/${location.id}/members/${id}`, body),
    onSuccess: () => {
      void message.success("変更した(監査ログに記録した)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns = [
    {
      title: "従業員",
      width: 280,
      render: (_: unknown, m: ReceptionMember) =>
        m.employee ? `${m.employee.name}(${m.employee.emp_code})` : `#${m.employee_id}`,
    },
    {
      title: "役割",
      width: 160,
      render: (_: unknown, m: ReceptionMember) =>
        m.active && !readOnly ? (
          <Select
            size="small"
            value={m.role}
            style={{ width: 120 }}
            options={ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.value }))}
            onChange={(role) => patch.mutate({ id: m.id, body: { role } })}
          />
        ) : (
          roleLabel(m.role)
        ),
    },
    {
      title: "期間",
      width: 400,
      render: (_: unknown, m: ReceptionMember) => (
        <span className="ui-cell-sub">
          {fmt(m.valid_from) || "—"} 〜 {fmt(m.valid_until) || "(期限なし)"}
        </span>
      ),
    },
    {
      title: "",
      width: 110,
      render: (_: unknown, m: ReceptionMember) =>
        m.active ? (
          readOnly ? null : (
            <Popconfirm
              title="担当を終わらせる"
              description="今の時刻で終わらせる(行は履歴として残る)"
              okText="終わらせる"
              cancelText="やめる"
              onConfirm={() => patch.mutate({ id: m.id, body: { valid_until: new Date().toISOString() } })}
            >
              <Button size="small">終わらせる</Button>
            </Popconfirm>
          )
        ) : (
          <StatusTag tone="neutral">終了</StatusTag>
        ),
    },
  ];

  return (
    <>
      {query.isError && <Alert type="error" showIcon message="拠点メンバーを取得できなかった" description={errorText(query.error)} />}
      <Table<ReceptionMember>
        scroll={{ x: "max-content" }}
        size="small"
        rowKey="id"
        loading={query.isFetching}
        dataSource={query.data?.items ?? []}
        columns={columns}
        pagination={false}
        locale={{ emptyText: "拠点メンバーがいない(この拠点の来訪は admin にしか見えない)" }}
        style={{ marginBottom: 16 }}
      />
      {!readOnly && (
        <Form<MemberForm>
          form={form}
          layout="inline"
          initialValues={{ role: "operator" }}
          disabled={add.isPending}
          onFinish={(v) => add.mutate(v)}
        >
          <Form.Item name="employee_id" rules={[{ required: true, message: "従業員を選ぶ" }]}>
            <EmployeePicker />
          </Form.Item>
          <Form.Item name="role">
            <Select style={{ width: 120 }} options={ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.value }))} />
          </Form.Item>
          <Form.Item name="valid_until">
            <DatePicker showTime placeholder="終了(任意)" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={add.isPending}>
              足す
            </Button>
          </Form.Item>
        </Form>
      )}
      <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
        拠点での役割は、その人のロールで頭打ちになる(reception:viewer の人を operator にしても viewer の範囲)。
      </Text>
    </>
  );
}

function TestChannels({ location }: { location: ReceptionLocation }) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const test = useMutation({
    mutationFn: () => send<{ results: ChannelResult[] }>("POST", `/locations/${location.id}/test-channels`),
    onSuccess: () => invalidate(),
    onError: (e) => void message.error(errorText(e)),
  });
  return (
    <>
      <p>
        受付窓口のメール(<span className="ui-num">{location.desk_email}</span>)へテストのメールを 1 通送る。
        送信元は設定の「メール」(バウンスの受け箱・差出人名)と、全体の SMTP 設定を使う。
      </p>
      <p>
        端末の登録には、このテスト送信の受理が要る。受付窓口のメールや配送の設定を変えたら、送り直す。
      </p>
      <Alert
        style={{ marginBottom: 12 }}
        showIcon
        type={location.desk_verified ? "success" : "warning"}
        message={
          location.desk_verified ? (
            <>
              受理済み(<TimeAgo iso={location.desk_verified_at} />)
            </>
          ) : (
            "未確認(この拠点には端末を登録できない)"
          )
        }
      />
      <Button onClick={() => test.mutate()} loading={test.isPending}>
        テスト送信
      </Button>
      {test.data?.results.map((r) => (
        <Alert
          key={r.channel}
          style={{ marginTop: 12 }}
          showIcon
          type={r.ok ? "success" : "error"}
          message={`${r.channel}: ${r.outcome}`}
          description={r.detail || undefined}
        />
      ))}
    </>
  );
}
